import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "src/redux/store";
import ATMPagination from "src/components/UI/atoms/ATMPagination/ATMPagination";
import ATMTable, {
  columnTypes,
} from "src/components/UI/atoms/ATMTable/ATMTable";
import ATMTableHeader from "src/components/UI/atoms/ATMTableHeader/ATMTableHeader";
import {
  setRowsPerPage,
  setPage,
  setSearchValue,
  setOrderByValue,
} from "src/redux/slices/PANApplication/PANGenerateApplicationSlice";
import { PANListResponse } from "src/models/PAN.model";
import AuthHOC from "src/userAccess/AuthHOC";
import { AccessAction } from "src/utils/Enums/AccessAction";
import { UserData } from "src/components/UI/Header/Header";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";

type Props = {
  columns: columnTypes[];
  rows: any[];
  /** CHANGE_ASSIGNEE. Gates the selection column and the bulk action together. */
  canChangeAssignee: boolean;
  selectedRows: PANListResponse[];
  setSelectedRows: React.Dispatch<React.SetStateAction<PANListResponse[]>>;
  isBulkAssigning: boolean;
  onBulkAssign: () => void;
  /** PAN_GENERATE_AUTO_DOWNLOAD. Also turns the selection column on. */
  canAutoDownload: boolean;
  isAutoDownloading: boolean;
  onAutoDownload: () => void;
};

const PANGenerateApplicationListing = ({
  columns,
  rows,
  canChangeAssignee,
  selectedRows,
  setSelectedRows,
  isBulkAssigning,
  onBulkAssign,
  canAutoDownload,
  isAutoDownloading,
  onAutoDownload,
}: Props) => {
  // Either bulk action needs the ticks; each button below shows for its own grant.
  const canSelect = canChangeAssignee || canAutoDownload;

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const PANGenerateApplicationState: any = useSelector(
    (state: RootState) => state.PANGenerateApplication
  );

  const userData: UserData =
    localStorage.getItem("userData") !== "undefined" &&
    JSON.parse(localStorage.getItem("userData") || "{}");

  const userAccess = JSON.parse(localStorage.getItem("userAccess") || "{}");

  const {
    page,
    rowsPerPage,
    isTableLoading,
    searchValue,
    totalItems,
    orderByValue,
  } = PANGenerateApplicationState;

  return (
    <div className="border flex flex-col overflow-auto rounded bg-white ">
      {/*Table Header */}
      <ATMTableHeader
        page={page}
        rowCount={totalItems}
        rowsPerPage={rowsPerPage}
        rows={rows}
        onRowsPerPageChange={(newValue) => dispatch(setRowsPerPage(newValue))}
        searchValue={searchValue}
        onSearchChange={(newValue) => dispatch(setSearchValue(newValue))}
        orderValue={orderByValue}
        onOrderByChange={(newValue) => dispatch(setOrderByValue(newValue))}
        isShowOrder={true}
      />

      {/*
        Bulk action bar. It only takes up space once something is ticked, so the
        tab looks unchanged for admins who never use it — and it is absent
        entirely without CHANGE_ASSIGNEE or AUTO DOWNLOAD, since the checkboxes
        that feed it are gated on the same grants. Each button shows only for
        its own grant.

        Deliberately the tab's own colour rather than the amber the FORM FILL
        PENDING skip bar uses: two bulk actions that look alike are two bulk
        actions someone will confuse.
      */}
      {canSelect && selectedRows?.length ? (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t bg-slate-50">
          <span className="text-[13px] text-slate-600">
            {selectedRows.length} application
            {selectedRows.length > 1 ? "s" : ""} selected
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-[13px] text-slate-500 underline"
              onClick={() => setSelectedRows([])}
            >
              Clear
            </button>

            {canAutoDownload ? (
              <ATMLoadingButton
                type="button"
                loadingText="Please wait..."
                isLoading={isAutoDownloading}
                disabled={isAutoDownloading}
                className="bg-green-600 w-40 p-0 py-[6px] text-[12px]"
                onClick={onAutoDownload}
              >
                AUTO DOWNLOAD
              </ATMLoadingButton>
            ) : null}

            {canChangeAssignee ? (
              <ATMLoadingButton
                type="button"
                loadingText="Please wait..."
                isLoading={isBulkAssigning}
                disabled={isBulkAssigning}
                className="bg-primary-main w-40 p-0 py-[6px] text-[12px]"
                onClick={onBulkAssign}
              >
                CHANGE ASSIGNEE
              </ATMLoadingButton>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Table */}
      <div className="border flex flex-col grow overflow-auto rounded bg-white">
        <ATMTable
          columns={columns}
          isLoading={isTableLoading}
          rows={rows}
          isCheckbox={canSelect}
          selectedRows={selectedRows}
          onRowSelect={(selectedRows) => setSelectedRows(selectedRows)}
          disableRowClick={
            !AuthHOC({
              type: "ACTION",
              moduleName: "PAN_APPLICATIONS",
              action: AccessAction.VIEW,
              resultType: "BOOLEAN",
            })
          }
          rowExtraClasses={(row) => "min-h-[40px]"}
          onRowClick={(row: PANListResponse) => {
            if (userData?.type === "SUPER_ADMIN") {
              navigate(`/pan/${row._id}`);
            } else if (row?.assignedToId === userAccess?._id) {
              navigate(`/pan/${row._id}`);
            } else if (
              // VIEW ALL ENTRIES lists other users' entries too, so they open as well.
              AuthHOC({
                type: "ACTION",
                moduleName: "PAN_APPLICATIONS",
                action: AccessAction.VIEW_ALL_ENTRIES,
                resultType: "BOOLEAN",
              })
            ) {
              navigate(`/pan/${row._id}`);
            } else {
              navigate(`/pan/VERIFY}`);
            }
          }}
        />
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end border-t border-slate-300">
        <ATMPagination
          page={page}
          rowCount={totalItems}
          rows={rows}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => dispatch(setPage(newPage))}
        />
      </div>
    </div>
  );
};

export default PANGenerateApplicationListing;
